/**
 * Shared time-axis helpers for history charts (asset + tracker). Extracted from
 * the asset history graph's useChartOptions so both charts render identical
 * axes, midnight dividers, and 3-line time labels.
 */
import { DateTime } from 'luxon'

/** Visible window as [startMs, endMs]. */
export type PeriodWindow = [number, number]

/** Compute midnight timestamps within a range, in the given timezone, so the
 *  day-divider lines align with the zone-aware date labels on the axis. */
export function getMidnights(start: number, end: number, zone: string): number[] {
  const midnights: number[] = []
  let d = DateTime.fromMillis(start, { zone }).startOf('day').plus({ days: 1 })
  while (d.toMillis() <= end) {
    midnights.push(d.toMillis())
    d = d.plus({ days: 1 })
  }
  return midnights
}

/** Tick interval for a window span: hourly ≤24h, 6-hourly ≤72h, else daily. */
export const timeAxisInterval = (periodSpanHours: number): number =>
  periodSpanHours <= 24 ? 3600000 : periodSpanHours <= 72 ? 6 * 3600000 : 24 * 3600000

/**
 * Tick positions aligned to the profile zone's midnight, every `intervalMs` within each
 * day. ECharts aligns its own time ticks to the BROWSER's midnight, so when the browser
 * and profile zones differ every tick lands off midnight (e.g. "01" each day) and the
 * date line — drawn only at midnight — never appears. Stepping per calendar day keeps
 * the ticks on the zone's hours across DST changes.
 */
export function zoneAlignedTicks(start: number, end: number, zone: string, intervalMs: number): number[] {
  const stepHours = Math.max(1, Math.round(intervalMs / 3600000))
  const ticks: number[] = []
  for (
    let day = DateTime.fromMillis(start, { zone }).startOf('day');
    day.toMillis() <= end;
    day = day.plus({ days: 1 })
  ) {
    for (let hour = 0; hour < 24; hour += stepHours) {
      const t = day.set({ hour }).toMillis()
      if (t >= start && t <= end) ticks.push(t)
    }
  }
  return ticks
}

/** Three-line label: time on top, then date, then tz abbreviation. Date and tz only
 *  render at midnight, but the lower lines are always present (blank placeholders) so
 *  every tick reserves the same vertical space and the time labels don't shrink. */
export const threeLineTimeFormatter =
  (zone: string) =>
  (value: number): string => {
    const d = DateTime.fromMillis(value, { zone })
    const hour = d.toFormat('HH')
    const isMidnight = d.hour === 0 && d.minute === 0
    const date = isMidnight ? d.toFormat('LLL d') : ' '
    const tz = isMidnight ? `(${d.toFormat('ZZZZ')})` : ' '
    return `${hour}\n${date}\n${tz}`
  }

/** Build a time x-axis for the given grid; only the labelled axis draws the 3-line labels. */
export const makeTimeAxis = (opts: {
  gridIndex: number
  showLabel: boolean
  periodWindow: PeriodWindow
  zone: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): any => {
  const interval = timeAxisInterval((opts.periodWindow[1] - opts.periodWindow[0]) / 3600000)
  // Labels, ticks and split lines all follow these (split lines read the tick model).
  const ticks = zoneAlignedTicks(opts.periodWindow[0], opts.periodWindow[1], opts.zone, interval)
  return {
    type: 'time',
    gridIndex: opts.gridIndex,
    min: opts.periodWindow[0],
    max: opts.periodWindow[1],
    axisLabel: opts.showLabel
      ? {
          show: true,
          hideOverlap: true,
          formatter: threeLineTimeFormatter(opts.zone),
          lineHeight: 14,
          customValues: ticks,
        }
      : { show: false },
    axisTick: { show: false, customValues: ticks },
    axisLine: { show: false },
    splitLine: { show: true, lineStyle: { color: '#d1d5db', opacity: 0.5 } },
    minInterval: interval,
    maxInterval: interval,
  }
}

/** Vertical day-divider markLines on an invisible series, or null when the window
 *  contains no midnight. Date labels live on the axis itself. */
export const buildMidnightMarkLineSeries = (
  periodWindow: PeriodWindow,
  zone: string,
  xAxisIndex = 0,
  yAxisIndex = 0
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any | null => {
  const midnights = getMidnights(periodWindow[0], periodWindow[1], zone)
  if (!midnights.length) return null
  return {
    type: 'line',
    name: '__midnight__',
    xAxisIndex,
    yAxisIndex,
    data: [],
    silent: true,
    symbol: 'none',
    lineStyle: { width: 0 },
    markLine: {
      silent: true,
      symbol: 'none',
      label: { show: false },
      data: midnights.map((ts) => ({
        xAxis: ts,
        lineStyle: { color: '#000', type: 'solid', width: 1 },
      })),
    },
  }
}
